/**
 * THE OFFICE FOLLOW-UPS. What somebody said she would do, and by when.
 *
 * `tasks` has been written to since the phone seam was built and read by nothing at all: a missed
 * call from a household opens a `follow_up` row titled "Call back", and the only reason the office
 * ever saw one was the `task_due` notification raised beside it. A durable row nobody looks at is
 * not something anybody sees. `docs/handover.md` section 4 item 10 asked for this screen, and with
 * it the notification goes back to being a nudge rather than the whole mechanism.
 *
 * OPEN FIRST, AND THE LATE ONES ABOVE THEM. The server sends the list in exactly that order and
 * `web/src/tasks.ts` transcribes it, so the three captions on an open list are the ladder read out
 * loud: late, still to do, no day set. Nothing here re-sorts what the server sorted.
 *
 * ONE THING IS CORAL, and it is the due day of a task that is late. An overdue follow-up IS a gap
 * somebody has to close, which is the one meaning coral carries in this interface. A task nobody
 * has been given is NOT one: most follow-ups in this business belong to whoever gets to them, and
 * a list painted coral down its whole length says nothing at all. The day is measured against the
 * server's own day, so a phone in another zone cannot make a task late a morning early.
 *
 * TWO SHAPES OF ONE LIST, the same as every other list screen. On a phone the rows are cards, each
 * carrying its own form behind a disclosure, because a panel that covers the row it is about is the
 * wrong answer in a hand. From md up the same rows are a table and the row you press is worked in
 * the panel on the right. `DataTable` draws both from ONE array.
 *
 * A FOLLOW-UP ABOUT AN INVOICE OPENS IT. An answer below happy to "How did we do?" raises one that
 * names its invoice (ops #46), and the invoice screen is where that question is held and released,
 * so the card and the panel carry the invoice one tap away. So does the chase an automatic charge
 * that did not go through raises (step 4): the office sends her the invoice from its screen.
 *
 * AN ASK FROM HOME OPENS IT, TO BE DECIDED (ops #19, step 7). A client who asks for something on her
 * household's own page raises a follow-up, and the follow-up carries the ask: what she asked in her
 * words, and Accept and Decline, in the panel and behind the card (`components/HomeRequest.tsx`).
 * Deciding it closes the follow-up.
 *
 * AND THE CALLS ARE THE SECOND TAB HERE, at `/tasks/calls`, rather than a word of their own in the
 * rail. "Who rang this morning" and "what did somebody say she would do about it" are the same
 * question asked twice, so they are two tabs of one screen and the rail stops at eight.
 */
import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  TASK_KINDS,
  TOUCH_OUTCOMES,
  type FollowUpChannel,
  type TaskKind,
  type TouchOutcome,
} from '@fk/shared';
import {
  ApiError,
  createTask,
  discardTask,
  listTasks,
  listUsers,
  patchTask,
  type Discard,
  type Me,
  type OfficeUser,
  type TaskFilter,
  type TaskList,
  type TaskRow,
} from '../api.ts';
import { Card, Detail, MEASURE, Pill, ScreenTitle } from '../components/AppShell.tsx';
import { CallLink } from '../components/CallLink.tsx';
import { MarkPills } from '../components/DoNotContact.tsx';
import { DiscardControl } from '../components/Discard.tsx';
import { Button, SelectField, TextAreaField, TextField } from '../components/Field.tsx';
import { HouseholdPicker } from '../components/HouseholdPicker.tsx';
import {
  DataTable,
  FilterBar,
  FilterSelect,
  PanelRule,
  SidePanel,
  SplitLayout,
  useBesideView,
  useWideView,
  type DataColumn,
} from '../components/ListView.tsx';
import { Busy, EmptyNotice, ErrorNotice, SavedNotice } from '../components/Notice.tsx';
import { HomeRequest, type Switches } from '../components/HomeRequest.tsx';
import { GapPill } from '../components/StatusPills.tsx';
import { CheckField } from '../components/VisitBits.tsx';
import { formatInstant } from '../format.ts';
import { TASK_KIND_LABELS, TASK_STATE_LABELS, TOUCH_OUTCOME_LABELS } from '../labels.ts';
import { officePeople } from '../people.ts';
import {
  NOTHING_TYPED,
  dueDayWords,
  newTaskBody,
  taskEdits,
  taskGroups,
  taskIsOverdue,
  taskProblem,
  typedTaskOf,
  whoHasItWords,
  type TypedTask,
} from '../tasks.ts';

/** The three answers the door takes, in the words somebody at a desk would choose between. */
const FILTERS: readonly TaskFilter[] = ['open', 'done', 'all'];

const FILTER_LABELS: Record<TaskFilter, string> = Object.freeze({
  open: 'Still to do',
  done: 'Finished',
  all: 'Everything',
});

/**
 * What a typed follow-up is filed as unless somebody says otherwise. Office work is what most of
 * them are. The picker below offers all five kinds, `renewal` among them: nothing raises a renewal
 * on a schedule, so the only way one exists is somebody choosing it here.
 */
const FIRST_KIND: TaskKind = 'admin';

/**
 * THE THREE TABS. All three are this one screen, and the rail knows only the first: `/tasks` is
 * active for any of them, so pressing Calls or Texts does not put the navigation out of step with
 * the page. Texts lives at `/messages`, outside `/tasks`, so the rail's Tasks word names that path
 * as its own (`also` in `App.tsx`) rather than leaning on the prefix. The row wraps on a phone.
 */
export function OfficeTabs({ here }: { here: 'tasks' | 'calls' | 'texts' }) {
  const tabs = [
    { to: '/tasks', key: 'tasks', label: 'Follow-ups' },
    { to: '/tasks/calls', key: 'calls', label: 'Calls' },
    { to: '/messages', key: 'texts', label: 'Texts' },
  ] as const;
  return (
    <div
      className="mb-4 flex flex-wrap gap-2"
      role="navigation"
      aria-label="Follow-ups, calls and texts"
    >
      {tabs.map((tab) => (
        <Link
          key={tab.key}
          to={tab.to}
          aria-current={tab.key === here ? 'page' : undefined}
          className={[
            'inline-flex min-h-11 desk:min-h-9 items-center rounded-lg border px-4 text-sm font-medium',
            tab.key === here
              ? 'border-navy bg-navy text-cream'
              : 'border-navy/25 bg-parchment text-navy',
          ].join(' ')}
        >
          {tab.label}
        </Link>
      ))}
    </div>
  );
}

export function Tasks({ me }: { me: Me }) {
  const wide = useWideView();
  const beside = useBesideView();
  const [filter, setFilter] = useState<TaskFilter>('open');
  const [list, setList] = useState<TaskList | null>(null);
  const [people, setPeople] = useState<readonly OfficeUser[]>([]);
  const [error, setError] = useState<ApiError | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [typing, setTyping] = useState(false);
  const [said, setSaid] = useState<string | null>(null);

  const reload = useCallback(() => setAttempt((n) => n + 1), []);

  useEffect(() => {
    let live = true;
    setError(null);
    setList(null);
    listTasks(filter)
      .then((answer) => {
        if (live) setList(answer);
      })
      .catch((thrown: unknown) => {
        if (!live) return;
        setError(
          thrown instanceof ApiError
            ? thrown
            : new ApiError(0, 'error', 'The follow-ups did not load.'),
        );
      });
    return () => {
      live = false;
    };
  }, [filter, attempt]);

  /**
   * The names in the office, asked for once and shared by every picker on the screen. Three or
   * four rows that do not change while somebody is looking at a list, and one request rather than
   * one per open panel: Workers Free allows fifty subrequests per invocation.
   *
   * AND THE FILTER IS THE SCREEN'S, not the read's. `GET /api/users` answers with everybody
   * enrolled since the suspend door was built, cleaners and suspended accounts included, because
   * the panel that shuts a sign-in has to be able to show the rows it acts on. Offering one of
   * those here would be offering a save the server refuses: `officePeople` is the same predicate
   * `api/src/lib/office.ts` keeps, applied where the names arrive so every picker below shares it.
   */
  useEffect(() => {
    let live = true;
    listUsers()
      .then((answer) => {
        if (live) setPeople(officePeople(answer.users));
      })
      .catch(() => {
        // The picker offers nothing but "nobody in particular" and says so under itself. A screen
        // that will not draw because a list of four names did not arrive is worse than a screen
        // where one field is briefly out of use, and every row on it still reads and still saves.
      });
    return () => {
      live = false;
    };
  }, []);

  const changed = useCallback(
    (sentence: string) => {
      setSaid(sentence);
      reload();
    },
    [reload],
  );

  const rows = list?.tasks ?? [];
  const today = list?.today ?? me.today;
  const groups = taskGroups(rows, today);
  const selected = rows.find((row) => row.id === selectedId) ?? null;
  /*
   * With the panel beside the list from xl, the kind rides under the title instead of taking a
   * column of its own (ops #56): at 1366 the five columns needed 712px beside a panel that left
   * 648, and the kind is the word the panel beside already says of the one being worked.
   */
  const merged = beside && selected !== null;

  const kind: readonly DataColumn<TaskRow>[] = merged
    ? []
    : [
        {
          key: 'kind',
          width: '132px',
          cellClassName: 'text-navy/75',
          header: 'What kind',
          cell: (row) => TASK_KIND_LABELS[row.kind],
        },
      ];

  const columns: readonly DataColumn<TaskRow>[] = [
    {
      key: 'title',
      header: 'What has to be done',
      cell: (row) => (
        <button
          type="button"
          onClick={() => setSelectedId(row.id)}
          aria-pressed={row.id === selectedId}
          className="flex min-h-11 w-full flex-col justify-center text-left font-semibold desk:min-h-9"
        >
          {/* The size rides on the span, never on the button: styles.css sets 16px on every
              control outside a cascade layer, and an unlayered declaration beats the layered
              utility, so `text-sm` on the button itself would not render. The title wraps rather
              than being cut at one line, which lost the point of it (the walk of 2026-09-23); the
              detail is two lines at most, and the whole of it is a press away in the panel. */}
          <span className="min-w-0 text-sm break-words">{row.title}</span>
          {row.detail ? (
            <span className="line-clamp-2 text-xs font-normal break-words text-navy/65">
              {row.detail}
            </span>
          ) : null}
          {merged ? (
            <span className="text-xs font-normal text-navy/65">{TASK_KIND_LABELS[row.kind]}</span>
          ) : null}
        </button>
      ),
    },
    ...kind,
    {
      key: 'household',
      width: '150px',
      header: 'Household',
      // In the face and size of the rest of the row, and wrapped rather than cut off: the name is
      // one column of a follow-up here, not the heading it is on the money list (the walk of
      // 2026-09-23).
      cell: (row) =>
        row.clientId ? (
          <Link
            to={`/clients/${row.clientId}`}
            className="flex min-h-11 desk:min-h-9 items-center text-sm break-words underline-offset-2 hover:underline"
          >
            {row.clientName ?? 'A household'}
          </Link>
        ) : (
          <span className="text-navy/50">Nobody in particular</span>
        ),
    },
    {
      key: 'who',
      width: '132px',
      cellClassName: 'text-navy/75',
      header: 'Given to',
      cell: (row) =>
        row.assignedToName ?? <span className="text-navy/50">{whoHasItWords(null)}</span>,
    },
    {
      key: 'due',
      width: '138px',
      header: 'Due',
      cell: (row) => <DueWords row={row} today={today} />,
    },
  ];

  return (
    <div>
      <ScreenTitle title="Tasks" subtitle="What somebody said they would do, and by when." />
      <OfficeTabs here="tasks" />

      <FilterBar
        action={
          <Button onClick={() => setTyping((open) => !open)} full>
            Add a task
          </Button>
        }
      >
        <FilterSelect
          id="tasks-filter"
          label="Show"
          value={filter}
          onChange={(chosen) => {
            if (chosen !== '') setFilter(chosen);
          }}
          options={FILTERS}
          toLabel={(option) => FILTER_LABELS[option]}
          className="md:w-[170px]"
        />
      </FilterBar>

      {typing ? (
        <Card className="mb-4">
          <AddATask
            people={people}
            onSaved={(sentence) => {
              setTyping(false);
              changed(sentence);
            }}
            onCancel={() => setTyping(false)}
          />
        </Card>
      ) : null}

      {said ? (
        <div className="mb-4">
          <SavedNotice>{said}</SavedNotice>
        </div>
      ) : null}

      {error ? (
        <ErrorNotice title="The follow-ups did not load" message={error.message} onRetry={reload} />
      ) : list === null ? (
        <Busy what="the follow-ups" />
      ) : rows.length === 0 ? (
        <EmptyNotice title={filter === 'done' ? 'Nothing finished yet' : 'Nothing on the list'}>
          <p>
            {filter === 'open'
              ? 'Every follow-up is done. A call nobody answered puts one here on its own, and the form above types one in.'
              : filter === 'done'
                ? 'Nothing has been marked done. Choose what is still to do, above, to see what is waiting.'
                : 'Nothing has been written down yet. The form above types the first one in.'}
          </p>
        </EmptyNotice>
      ) : (
        <SplitLayout
          panel={
            wide && selected ? (
              // Keyed by the row, so pressing another follow-up draws a panel with that row's own
              // boxes rather than the last one's typing under a new title.
              <TaskPanel
                key={selected.id}
                row={selected}
                today={today}
                people={people}
                me={me}
                onChanged={changed}
                onClose={() => setSelectedId(null)}
              />
            ) : undefined
          }
        >
          <DataTable
            label="Follow-ups"
            groups={groups}
            columns={columns}
            rowKey={(row) => row.id}
            rowClassName={(row) =>
              row.id === selectedId
                ? 'bg-gold/10 [&>td:first-child]:border-l-[3px] [&>td:first-child]:border-l-gold [&>td:first-child]:pl-[13px]'
                : ''
            }
            card={(row) => (
              <TaskCard
                row={row}
                today={today}
                people={people}
                me={me}
                onChanged={changed}
                actions={!wide}
              />
            )}
          />

          {rows.length >= list.limit ? (
            <p className={`mt-2 text-xs text-navy/60 ${MEASURE}`}>
              The first {list.limit} are shown. A list this long is a list nobody is working: finish
              some, or throw away what was never real.
            </p>
          ) : null}
        </SplitLayout>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * The words a column and a card say about the one gap on this screen.
 * ------------------------------------------------------------------ */

/** The due day. Coral only where it is late, which is the one gap a follow-up can have. */
function DueWords({ row, today }: { row: TaskRow; today: string }) {
  const late = taskIsOverdue(row, today);
  const words = dueDayWords(row, today);
  if (late) return <span className="font-medium text-coral">{words}</span>;
  if (row.dueOn === null) return <span className="text-navy/50">{words}</span>;
  return <span>{words}</span>;
}

/** The invoice a follow-up is about, one tap away, or nothing for a follow-up about none. */
function invoiceLink(row: TaskRow) {
  return row.invoiceId ? (
    <Link to={`/invoices/${row.invoiceId}`} className="underline">
      The invoice
    </Link>
  ) : row.enquiry ? (
    <Link to={`/enquiries?open=${encodeURIComponent(row.enquiry.id)}`} className="underline">
      The enquiry: {row.enquiry.name}
    </Link>
  ) : null;
}

/**
 * WHO TO RING FOR A QUOTE'S FOLLOW-UP (ops #86), with the register read again as the list was shown:
 * a number marked do not call is drawn as one, and nothing at all once the step is not open.
 */
function enquiryReach(row: TaskRow) {
  if (!row.enquiry || row.state !== 'open') return null;
  const { phoneE164, email, doNotContact } = row.enquiry;
  if (!phoneE164 && !email) return null;
  return (
    <div className="mt-2 flex flex-wrap items-center gap-2">
      {phoneE164 ? <CallLink phoneE164={phoneE164} marked={doNotContact.call ?? null} /> : null}
      <MarkPills marks={doNotContact} />
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * One follow-up on a phone, with its form inside it.
 * ------------------------------------------------------------------ */

function TaskCard({
  row,
  today,
  people,
  me,
  onChanged,
  actions,
}: {
  row: TaskRow;
  today: string;
  people: readonly OfficeUser[];
  /** Ops's two switches, which the ask from home says before Accept. */
  me: Switches;
  onChanged: (sentence: string) => void;
  /** False from md up, where the same form is drawn once in the panel instead. */
  actions: boolean;
}) {
  const late = taskIsOverdue(row, today);
  return (
    <Card className={late ? 'border-coral/40' : ''}>
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="font-serif text-lg leading-tight">{row.title}</h2>
        {/* Two pills, two questions: what kind of thing this is, and whether it is finished. One
            pill coloured by the state would have said both badly, and a kind reading "Office work"
            in sage is a colour carrying a fact the word does not. */}
        <Pill>{TASK_KIND_LABELS[row.kind]}</Pill>
        {row.state === 'done' ? <Pill tone="sage">{TASK_STATE_LABELS.done}</Pill> : null}
        {late ? <GapPill>{dueDayWords(row, today)}</GapPill> : null}
      </div>

      {row.detail ? <p className="mt-1 text-sm whitespace-pre-line">{row.detail}</p> : null}
      {enquiryReach(row)}

      <dl className="mt-1">
        <Detail label="Due" value={late ? null : dueDayWords(row, today)} />
        <Detail label="Given to" value={whoHasItWords(row.assignedToName)} />
        <Detail
          label="Household"
          value={
            row.clientId ? (
              <Link to={`/clients/${row.clientId}`} className="underline">
                {row.clientName ?? 'A household'}
              </Link>
            ) : null
          }
        />
        <Detail label="About" value={invoiceLink(row)} />
        <Detail label="Finished" value={formatInstant(row.completedAt)} />
      </dl>

      {actions && row.homeRequestId ? (
        <Disclosure summary="The ask from home">
          <div className="mt-3">
            <HomeRequest id={row.homeRequestId} me={me} onDecided={onChanged} />
          </div>
        </Disclosure>
      ) : null}

      {actions ? (
        <Disclosure summary={row.state === 'done' ? 'Reopen it or correct it' : 'Work on it'}>
          <TaskWork idPrefix="card" row={row} people={people} onChanged={onChanged} />
        </Disclosure>
      ) : null}
    </Card>
  );
}

/**
 * A block on a card that mounts nothing until somebody opens it, and never unmounts it again.
 * Closed until opened once, and from then on the browser hides it rather than React discarding
 * what was half typed into it. The same latch the enquiries cards use, for the same reason.
 */
function Disclosure({ summary, children }: { summary: string; children: ReactNode }) {
  const [opened, setOpened] = useState(false);
  return (
    <details
      className="mt-3 rounded-lg border border-navy/15 p-3"
      onToggle={(event) => {
        if (event.currentTarget.open) setOpened(true);
      }}
    >
      <summary className="min-h-11 desk:min-h-9 cursor-pointer text-sm font-medium">
        {summary}
      </summary>
      {opened ? children : null}
    </details>
  );
}

/* ------------------------------------------------------------------ *
 * The panel, from md up.
 * ------------------------------------------------------------------ */

function TaskPanel({
  row,
  today,
  people,
  me,
  onChanged,
  onClose,
}: {
  row: TaskRow;
  today: string;
  people: readonly OfficeUser[];
  /** Ops's two switches, which the ask from home says before Accept. */
  me: Switches;
  onChanged: (sentence: string) => void;
  onClose: () => void;
}) {
  const late = taskIsOverdue(row, today);
  return (
    <SidePanel
      title={row.title}
      onClose={onClose}
      pills={
        <>
          <Pill>{TASK_KIND_LABELS[row.kind]}</Pill>
          {row.state === 'done' ? <Pill tone="sage">{TASK_STATE_LABELS.done}</Pill> : null}
          {late ? <GapPill>{dueDayWords(row, today)}</GapPill> : null}
        </>
      }
    >
      <dl>
        <Detail label="Due" value={late ? null : dueDayWords(row, today)} />
        <Detail
          label="Household"
          value={
            row.clientId ? (
              <Link to={`/clients/${row.clientId}`} className="underline">
                {row.clientName ?? 'A household'}
              </Link>
            ) : null
          }
        />
        <Detail label="About" value={invoiceLink(row)} />
        <Detail label="Written down" value={formatInstant(row.createdAt)} />
        <Detail label="Finished" value={formatInstant(row.completedAt)} />
      </dl>
      {enquiryReach(row)}
      {/* A quote's follow-up carries its email written out, to copy into hello@ (ops #86). */}
      {row.enquiry && row.detail ? (
        <p className="mt-2 text-sm whitespace-pre-line">{row.detail}</p>
      ) : null}

      {row.homeRequestId ? (
        <>
          <PanelRule />
          <HomeRequest id={row.homeRequestId} me={me} onDecided={onChanged} />
        </>
      ) : null}

      <PanelRule />
      <TaskWork idPrefix="panel" row={row} people={people} onChanged={onChanged} />

      {/*
       * Last, under everything worth doing first, and quiet. A discard is not a gap somebody has
       * to close, so it is never coral, and it opens into a reason and a confirming press rather
       * than acting on the first tap.
       */}
      <PanelRule />
      <DiscardControl
        idPrefix={`task-${row.id}`}
        what="this task"
        doneSentence="Discarded. It is off the list, and why is on the record."
        hint="It leaves the list. The record and the reason stay on it."
        onDiscard={(body: Discard) => discardTask(row.id, body)}
        onDone={onChanged}
      />
    </SidePanel>
  );
}

/* ------------------------------------------------------------------ *
 * Working one: correct it, give it to somebody, finish it.
 * ------------------------------------------------------------------ */

function TaskWork({
  idPrefix,
  row,
  people,
  onChanged,
}: {
  /** Where this copy is drawn, because the card and the panel are both in the document. */
  idPrefix: string;
  row: TaskRow;
  people: readonly OfficeUser[];
  onChanged: (sentence: string) => void;
}) {
  const [typed, setTyped] = useState<TypedTask>(() => typedTaskOf(row));
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Done on a quote's follow-up (ops #86): which went, and what came of it.
  const [called, setCalled] = useState(false);
  const [emailed, setEmailed] = useState(false);
  const [outcome, setOutcome] = useState<TouchOutcome | ''>('');
  const field = (name: string) => `${idPrefix}-${name}-${row.id}`;
  const done = row.state === 'done';
  const followUp = Boolean(row.enquiry);

  /** On a refusal the server's sentence is shown and nothing typed is touched. */
  async function run(work: () => Promise<unknown>, sentence: string) {
    setBusy(true);
    setProblem(null);
    try {
      await work();
      onChanged(sentence);
    } catch (thrown) {
      setProblem(thrown instanceof ApiError ? thrown.message : 'That was not recorded.');
    } finally {
      setBusy(false);
    }
  }

  /** One patch carrying only what changed, so an untouched box never overwrites the row. */
  function save() {
    const wrong = taskProblem(typed);
    if (wrong) {
      setProblem(wrong);
      return;
    }
    const patch = taskEdits(typed, row);
    if (Object.keys(patch).length === 0) {
      setProblem('Nothing here has changed yet.');
      return;
    }
    void run(() => patchTask(row.id, patch), 'Saved.');
  }

  function finish() {
    const channels: FollowUpChannel[] = [
      ...(called ? (['phone'] as const) : []),
      ...(emailed ? (['email'] as const) : []),
    ];
    if (followUp && channels.length > 0 && outcome === '') {
      setProblem('Say what came of it: the call or the email you ticked needs an answer.');
      return;
    }
    void run(
      () =>
        patchTask(
          row.id,
          followUp && channels.length > 0
            ? { state: 'done', channels, outcome: outcome as TouchOutcome }
            : { state: 'done' },
        ),
      channels.length > 0
        ? 'Done, with what came of it recorded on the enquiry.'
        : 'Done, with your name and the moment on it.',
    );
  }

  function reopen() {
    void run(
      () => patchTask(row.id, { state: 'open' }),
      'Back on the list, and who finished it is off the record of it.',
    );
  }

  return (
    <div className="mt-3">
      {problem ? (
        <div className="mb-3">
          <ErrorNotice title="Not recorded" message={problem} />
        </div>
      ) : null}

      <TextField
        id={field('title')}
        label="What has to be done"
        required
        value={typed.title}
        onChange={(title) => setTyped((current) => ({ ...current, title }))}
        autoComplete="off"
      />
      <TextAreaField
        id={field('detail')}
        label="Anything more about it"
        value={typed.detail}
        onChange={(detail) => setTyped((current) => ({ ...current, detail }))}
        rows={2}
      />
      <div className="md:grid md:grid-cols-2 md:gap-3">
        <TextField
          id={field('due')}
          label="By which day"
          type="date"
          value={typed.dueOn}
          onChange={(dueOn) => setTyped((current) => ({ ...current, dueOn }))}
          hint="Leave it empty and it is on the list without a day, which is most of them."
        />
        <GiveItTo
          id={field('who')}
          people={people}
          value={typed.assignedTo}
          onChange={(assignedTo) => setTyped((current) => ({ ...current, assignedTo }))}
        />
      </div>

      <Button onClick={save} disabled={busy} full>
        {busy ? 'Recording' : 'Save'}
      </Button>

      <div className="mt-4 border-t border-navy/10 pt-3">
        {done ? (
          <>
            <Button tone="quiet" onClick={reopen} disabled={busy}>
              Put it back on the list
            </Button>
            <p className="mt-1 text-xs text-navy/65">
              Reopening it takes the name and the moment back off, so a row on today's list never
              carries who closed it last week.
            </p>
          </>
        ) : (
          <>
            {followUp ? (
              <fieldset className="mb-3">
                <legend className="text-sm font-medium">What went</legend>
                <div className="mt-1 flex flex-wrap gap-4">
                  <CheckField
                    id={field('called')}
                    label="I called"
                    checked={called}
                    onChange={setCalled}
                  />
                  <CheckField
                    id={field('emailed')}
                    label="I sent the email from hello@"
                    checked={emailed}
                    onChange={setEmailed}
                  />
                </div>
                {called || emailed ? (
                  <SelectField
                    id={field('outcome')}
                    label="What came of it"
                    value={outcome}
                    onChange={setOutcome}
                    options={TOUCH_OUTCOMES}
                    toLabel={(word) => TOUCH_OUTCOME_LABELS[word]}
                    placeholder="Choose one"
                    required
                  />
                ) : null}
                <p className="text-xs text-navy/65">
                  Recorded on the enquiry, never the words. A "no thanks" is a mark in the
                  do-not-contact register, which ends the follow-up.
                </p>
              </fieldset>
            ) : null}
            <Button onClick={finish} disabled={busy}>
              Mark it done
            </Button>
            <p className="mt-1 text-xs text-navy/65">
              Your name and this moment go on it, which is the whole evidence a follow-up happened.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * WHO IT IS GIVEN TO. Nobody is the first option and a real answer: most follow-ups here belong to
 * whoever gets to them, and a required picker would mean inventing an owner for every one of them.
 *
 * A cleaner is never in this list. The read behind it answers with everybody who has a sign-in, so
 * the screen narrows it to the office through `officePeople` before a picker ever sees it, and the
 * server refuses a cleaner again on the way in: a follow-up given to somebody who cannot open the
 * list is work nobody will do.
 */
function GiveItTo({
  id,
  people,
  value,
  onChange,
}: {
  id: string;
  people: readonly OfficeUser[];
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <SelectField
      id={id}
      label="Give it to"
      value={value}
      onChange={onChange}
      options={people.map((person) => person.id)}
      toLabel={(chosen) => people.find((person) => person.id === chosen)?.displayName ?? chosen}
      placeholder="Nobody in particular"
      hint={
        people.length === 0
          ? 'The names in the office did not load, so this can only be left as it is.'
          : undefined
      }
    />
  );
}

/* ------------------------------------------------------------------ *
 * Adding one.
 * ------------------------------------------------------------------ */

/**
 * A SMALL FORM, because a follow-up is written down in the ten seconds after somebody says she will
 * do it. Everything but the title is optional, and the household is searched rather than chosen
 * from a list: the client book is the thing this business grows, and a select over all of it is a
 * select nobody can find anybody in.
 */
function AddATask({
  people,
  onSaved,
  onCancel,
}: {
  people: readonly OfficeUser[];
  onSaved: (sentence: string) => void;
  onCancel: () => void;
}) {
  const [kind, setKind] = useState<TaskKind>(FIRST_KIND);
  const [typed, setTyped] = useState<TypedTask>(NOTHING_TYPED);
  const [clientId, setClientId] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    const wrong = taskProblem(typed);
    if (wrong) {
      setProblem(wrong);
      return;
    }
    setSaving(true);
    setProblem(null);
    try {
      await createTask(newTaskBody(kind, typed, clientId));
      onSaved('Written down. It is on the list until somebody marks it done.');
    } catch (thrown) {
      setProblem(thrown instanceof ApiError ? thrown.message : 'That task was not saved.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={submit} noValidate>
      <h2 className="font-serif text-lg">Add a task</h2>
      <p className="mt-1 mb-3 text-sm text-navy/70">
        The title is the only thing asked for. A day, a household and a name are each worth having
        and none of them is worth waiting for.
      </p>

      {problem ? (
        <div className="mb-4">
          <ErrorNotice title="Nothing was saved" message={problem} />
        </div>
      ) : null}

      <TextField
        id="new-task-title"
        label="What has to be done"
        required
        value={typed.title}
        onChange={(title) => setTyped((current) => ({ ...current, title }))}
        hint="Call them back about the oven. Send the quote. File the receipts."
        autoComplete="off"
      />
      <TextAreaField
        id="new-task-detail"
        label="Anything more about it"
        value={typed.detail}
        onChange={(detail) => setTyped((current) => ({ ...current, detail }))}
        rows={2}
      />

      <div className="md:grid md:grid-cols-2 md:gap-4">
        <SelectField
          id="new-task-kind"
          label="What kind"
          value={kind}
          onChange={setKind}
          options={TASK_KINDS}
          toLabel={(option) => TASK_KIND_LABELS[option]}
        />
        <TextField
          id="new-task-due"
          label="By which day"
          type="date"
          value={typed.dueOn}
          onChange={(dueOn) => setTyped((current) => ({ ...current, dueOn }))}
        />
        <GiveItTo
          id="new-task-who"
          people={people}
          value={typed.assignedTo}
          onChange={(assignedTo) => setTyped((current) => ({ ...current, assignedTo }))}
        />
        <HouseholdPicker
          idPrefix="new-task"
          label="Which household"
          hint="Left empty, it is a task about no household in particular."
          clientId={clientId}
          onChange={setClientId}
        />
      </div>

      <div className="flex flex-col gap-3 md:flex-row">
        <Button type="submit" full disabled={saving}>
          {saving ? 'Saving' : 'Write it down'}
        </Button>
        <Button tone="quiet" onClick={onCancel} disabled={saving}>
          Never mind
        </Button>
      </div>
    </form>
  );
}
